import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '@/pages/service/auth.service';

export interface NotificacionInvitacion {
    id: number;
    nombres: string;
    apellidos: string;
    identificacion: string;
    email: string;
    entidad_id: number;
    sede_id: number | null;
    invitacion: string;
    created_at: string;
    /** Marca local: si el usuario ya la vio en el panel. */
    leida?: boolean;
    /** Timestamp local de recepción (ISO). */
    recibida_en?: string;
}

const MAX_NOTIFICACIONES = 50;
const INTERVALO_MS = 10 * 60 * 1000; // 10 minutos

/**
 * Notificaciones de invitaciones en estado `por_confirmar`.
 *
 * Consulta `GET entidades/{id}/invitaciones-por-confirmar` al iniciar y luego
 * cada 10 minutos. Solo se activa para el usuario logueado sin sede asignada
 * (sede_id === null), que es quien confirma/cancela invitaciones.
 */
@Injectable({ providedIn: 'root' })
export class NotificacionesInvitacionService {
    private readonly _notificaciones = new BehaviorSubject<NotificacionInvitacion[]>([]);
    readonly notificaciones$ = this._notificaciones.asObservable();

    private readonly _sinLeer = new BehaviorSubject<number>(0);
    readonly sinLeer$ = this._sinLeer.asObservable();

    private entidadId: number | null = null;
    private storageKey = '';
    private activo = false;
    private pollSub?: Subscription;

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthService
    ) {}

    /** Arranca el sondeo si corresponde. Idempotente. */
    iniciar(): void {
        if (this.activo) return;

        const user = this.authService.user ? JSON.parse(this.authService.user) : null;
        const sinSede = user?.sede_id === null || user?.sede_id === undefined;
        if (!user || !sinSede || !user.entidad_id) return;

        this.entidadId = user.entidad_id;
        this.storageKey = `notif_invitaciones_${user.id}`;
        this.activo = true;
        this.restaurar();

        // Primera consulta inmediata y luego cada 10 minutos.
        this.pollSub = timer(0, INTERVALO_MS)
            .pipe(
                switchMap(() =>
                    this.http.get<any>(
                        `${environment.apiBack}entidades/${this.entidadId}/invitaciones-por-confirmar`
                    )
                )
            )
            .subscribe({
                next: (res) => {
                    const items: any[] = res?.data ?? res ?? [];
                    this.reconciliar(items);
                    console.info(`[Notificaciones] ${items.length} invitación(es) por confirmar`);
                },
                error: (err) => console.error('[Notificaciones] Error consultando invitaciones', err)
            });
    }

    /** Detiene el sondeo y limpia el estado en memoria (no borra el historial persistido). */
    detener(): void {
        this.pollSub?.unsubscribe();
        this.pollSub = undefined;
        this._notificaciones.next([]);
        this._sinLeer.next(0);
        this.activo = false;
        this.entidadId = null;
    }

    /** Fuerza una consulta inmediata (ej. tras confirmar/cancelar una invitación). */
    refrescar(): void {
        if (!this.activo || this.entidadId == null) return;
        this.http
            .get<any>(`${environment.apiBack}entidades/${this.entidadId}/invitaciones-por-confirmar`)
            .subscribe({
                next: (res) => this.reconciliar(res?.data ?? res ?? []),
                error: (err) => console.error('[Notificaciones] Error consultando invitaciones', err)
            });
    }

    marcarTodasLeidas(): void {
        const lista = this._notificaciones.value.map((n) => ({ ...n, leida: true }));
        this._notificaciones.next(lista);
        this.persistir(lista);
        this._sinLeer.next(0);
    }

    eliminar(id: number): void {
        const lista = this._notificaciones.value.filter((n) => n.id !== id);
        this._notificaciones.next(lista);
        this.persistir(lista);
        this.recalcularSinLeer(lista);
    }

    limpiar(): void {
        this._notificaciones.next([]);
        this.persistir([]);
        this._sinLeer.next(0);
    }

    /**
     * El backend es la fuente de verdad sobre qué invitaciones siguen por confirmar.
     * Se conservan las marcas locales (`leida`, `recibida_en`) de las que ya existían;
     * las nuevas entran como no leídas.
     */
    private reconciliar(items: any[]): void {
        const previas = new Map(this._notificaciones.value.map((n) => [n.id, n]));

        const lista: NotificacionInvitacion[] = (items ?? [])
            .filter((it) => it?.id)
            .map((it) => {
                const previa = previas.get(it.id);
                return {
                    ...it,
                    leida: previa?.leida ?? false,
                    recibida_en: previa?.recibida_en ?? it.created_at
                } as NotificacionInvitacion;
            })
            .sort((a, b) => (b.recibida_en ?? '').localeCompare(a.recibida_en ?? ''))
            .slice(0, MAX_NOTIFICACIONES);

        this._notificaciones.next(lista);
        this.persistir(lista);
        this.recalcularSinLeer(lista);
    }

    private recalcularSinLeer(lista: NotificacionInvitacion[]): void {
        this._sinLeer.next(lista.filter((n) => !n.leida).length);
    }

    private restaurar(): void {
        try {
            const raw = localStorage.getItem(this.storageKey);
            const lista: NotificacionInvitacion[] = raw ? JSON.parse(raw) : [];
            this._notificaciones.next(lista);
            this.recalcularSinLeer(lista);
        } catch {
            this._notificaciones.next([]);
            this._sinLeer.next(0);
        }
    }

    private persistir(lista: NotificacionInvitacion[]): void {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(lista));
        } catch {
            /* almacenamiento no disponible */
        }
    }
}
